// List of personal email domains that should be blocked
const PERSONAL_EMAIL_DOMAINS = [
  // Google - All variations
  'gmail.com',
  'googlemail.com',
  'googlemail.co.uk',
  'gmail.co.uk',
  
  // Microsoft
  'hotmail.com',
  'outlook.com',
  'live.com',
  'msn.com',
  'hotmail.co.uk',
  'outlook.co.uk',
  'live.co.uk',
  'hotmail.fr',
  'hotmail.de',
  'hotmail.es',
  'hotmail.it',
  'live.fr',
  'live.de',
  'live.co.uk',
  
  // Yahoo
  'yahoo.com',
  'yahoo.co.uk',
  'yahoo.ca',
  'yahoo.com.au',
  'yahoo.fr',
  'yahoo.de',
  'yahoo.es',
  'yahoo.it',
  'ymail.com',
  'rocketmail.com',
  
  // AOL
  'aol.com',
  'aim.com',
  'aol.co.uk',
  'aol.fr',
  'aol.de',
  
  // Apple
  'icloud.com',
  'me.com',
  'mac.com',
  
  // Other common personal email providers
  'protonmail.com',
  'proton.me',
  'tutanota.com',
  'mailinator.com',
  'guerrillamail.com',
  '10minutemail.com',
  'tempmail.org',
  'yopmail.com',
  'maildrop.cc',
  'sharklasers.com',
  'guerrillamailblock.com',
  
  // Regional providers
  'mail.ru',
  'yandex.com',
  'yandex.ru',
  'qq.com',
  '163.com',
  '126.com',
  'sina.com',
  'sohu.com',
  'naver.com',
  'daum.net',
  'hanmail.net',
  
  // Temporary/disposable email services
  'mailnesia.com',
  'throwaway.email',
  'getnada.com',
  'temp-mail.org',
  'fake-mail.org',
  'dispostable.com',
  'mytrashmail.com',
  'trashmail.com',
  'spamgourmet.com',
  'mailcatch.com',
  'mailexpire.com',
  'emailondeck.com',
];

/**
 * Enhanced validation for Google OAuth specifically
 * @param {string} email - The email address from Google OAuth
 * @returns {Object} - Validation result with isValid and message
 */
export const validateGoogleOAuthEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return {
      isValid: false,
      message: 'No email address received from Google'
    };
  }

  const emailLower = email.toLowerCase().trim();
  const emailParts = emailLower.split('@');
  
  if (emailParts.length !== 2) {
    return {
      isValid: false,
      message: 'Invalid email format received from Google'
    };
  }

  const domain = emailParts[1];
  const username = emailParts[0];

  // Specific Gmail/Google checks
  if (domain === 'gmail.com' || domain === 'googlemail.com') {
    return {
      isValid: false,
      message: 'Personal Gmail accounts are not allowed. Please use your business Google Workspace account with your company domain.'
    };
  }

  // Check for Gmail variations and international domains
  if (domain.includes('gmail') || domain.includes('googlemail')) {
    return {
      isValid: false,
      message: `Personal Google email domain (${domain}) is not allowed. Please use your business email address.`
    };
  }

  // Standard business email validation
  return validateBusinessEmail(email);
};

/**
 * Validates if an email domain is allowed (business email)
 * @param {string} email - The email address to validate
 * @returns {Object} - Validation result with isValid and message
 */
export const validateBusinessEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return {
      isValid: false,
      message: 'Please enter a valid email address'
    };
  }

  // Extract domain from email
  const emailLower = email.toLowerCase().trim();
  const emailParts = emailLower.split('@');
  
  if (emailParts.length !== 2) {
    return {
      isValid: false,
      message: 'Please enter a valid email address'
    };
  }

  const domain = emailParts[1];
  const username = emailParts[0];

  // Check if domain is in the blocked list
  if (PERSONAL_EMAIL_DOMAINS.includes(domain)) {
    return {
      isValid: false,
      message: `Personal email addresses (${domain}) are not allowed. Please use your business email address.`
    };
  }

  // Additional checks for suspicious domains
  if (domain.includes('temp') || domain.includes('disposable') || domain.includes('fake') || domain.includes('test')) {
    return {
      isValid: false,
      message: 'Temporary or disposable email addresses are not allowed. Please use your business email address.'
    };
  }

  // Check for suspicious patterns in domain
  if (domain.includes('mail') && (domain.includes('temp') || domain.includes('10min') || domain.includes('throw'))) {
    return {
      isValid: false,
      message: 'Temporary email services are not allowed. Please use your business email address.'
    };
  }

  // Check for single character domains or suspicious patterns
  if (domain.length < 4 || !domain.includes('.')) {
    return {
      isValid: false,
      message: 'Please enter a valid business email address'
    };
  }

  // Check for suspicious username patterns
  if (username.includes('test') || username.includes('temp') || username.includes('fake')) {
    return {
      isValid: false,
      message: 'Test or temporary email addresses are not allowed. Please use your business email address.'
    };
  }

  // Check for too generic business domains that might be personal
  const suspiciousDomains = ['example.com', 'test.com', 'localhost', 'domain.com'];
  if (suspiciousDomains.includes(domain)) {
    return {
      isValid: false,
      message: 'Please use a valid business email address'
    };
  }

  return {
    isValid: true,
    message: 'Business email address is valid'
  };
};

/**
 * Gets a user-friendly error message for blocked email domains
 * @param {string} domain - The blocked domain
 * @returns {string} - User-friendly error message
 */
export const getEmailDomainErrorMessage = (domain) => {
  const commonDomains = {
    'gmail.com': 'Gmail',
    'googlemail.com': 'Gmail',
    'hotmail.com': 'Hotmail',
    'outlook.com': 'Outlook',
    'yahoo.com': 'Yahoo',
    'icloud.com': 'iCloud',
    'aol.com': 'AOL',
    'live.com': 'Microsoft Live'
  };

  const providerName = commonDomains[domain] || domain;
  
  return `${providerName} is a personal email provider. Please use your business or corporate email address to access SOCFirst AI Platform.`;
};

/**
 * Checks if an email is from a business domain
 * @param {string} email - The email address to check
 * @returns {boolean} - True if business email, false if personal
 */
export const isBusinessEmail = (email) => {
  const validation = validateBusinessEmail(email);
  return validation.isValid;
};

/**
 * Gets suggestions for business email alternatives
 * @param {string} email - The personal email address
 * @returns {string} - Suggestion message
 */
export const getBusinessEmailSuggestion = (email) => {
  return "Please contact your IT administrator or use your company email address. Business email addresses typically end with your company domain (e.g., yourname@yourcompany.com).";
};

/**
 * Special validation for known business email patterns
 * @param {string} email - The email address to validate
 * @returns {boolean} - True if it matches business patterns
 */
export const isLikelyBusinessEmail = (email) => {
  if (!email) return false;
  
  const domain = email.toLowerCase().split('@')[1];
  if (!domain) return false;
  
  // Common business domain patterns
  const businessPatterns = [
    /\.corp$/,
    /\.inc$/,
    /\.llc$/,
    /\.ltd$/,
    /\.company$/,
    /\.org$/,
    /\.edu$/,
    /\.gov$/,
    /\.mil$/,
    /\.biz$/,
    /\.info$/,
    /\.pro$/
  ];
  
  return businessPatterns.some(pattern => pattern.test(domain));
};

const emailValidationUtils = {
  validateBusinessEmail,
  validateGoogleOAuthEmail,
  getEmailDomainErrorMessage,
  isBusinessEmail,
  getBusinessEmailSuggestion,
  isLikelyBusinessEmail,
  PERSONAL_EMAIL_DOMAINS
};

export default emailValidationUtils; 